import {
  AppBar,
  Box,
  Container,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import type { SxProps } from '@mui/system';
import type { JSX } from 'react';
import { BREAKPOINTS } from '@tbd/life-tokens';
import { theme } from '@tbd/lift-ui/mui';

import { useRequiredUser } from '#app/hooks/useUser.js';
import { ProfileMenu } from './ProfileMenu.js';

interface UserDetailLayoutProps {
  children: React.ReactNode;
  title: string;
  backButton?: JSX.Element;
}

// TODO: LIFT-480 replace this component in $userId.change-password with <UserRouteContainer /> and <UserTopBar />
export function UserDetailLayout(props: UserDetailLayoutProps) {
  return (
    <>
      <UserTopBar title={props.title} backButton={props.backButton} />
      <Container maxWidth="sm" component="main" sx={{ mb: theme.spacing(8) }}>
        {props.children}
      </Container>
    </>
  );
}

// TODO: LIFT-480 replace <UserRouteContainer />, <UserTopBar />, and <UserDetailLayout /> with a layout route at /users/users.tsx
// this file can be deleted after we create the layout route
export const UserRouteContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <Container
      maxWidth="sm"
      component="main"
      sx={{ mb: theme.spacing(8), mt: { xs: 2, sm: 3 } }}
    >
      {children}
    </Container>
  );
};

export function UserTopBar(
  props: Pick<UserDetailLayoutProps, 'title' | 'backButton'> & { sx?: SxProps }
) {
  const theme = useTheme();
  const { title, sx } = props;
  const user = useRequiredUser();
  const ICON_WIDTH = 36 + 16; // 36px wide + 16px padding

  return (
    <AppBar
      position="sticky"
      sx={{ top: 0, backgroundColor: 'white', mb: { xs: 2, sm: 3 }, ...sx }}
      color="transparent"
    >
      <Container maxWidth="xxl">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
          {/* keep the same width to prevent a shift when the back button appears */}
          <Box sx={{ minWidth: ICON_WIDTH }}>{props.backButton ?? ' '}</Box>

          <Typography
            noWrap
            component="div"
            sx={{
              ...theme.typography['component-bold-m'],
              width: 'calc(100vw - 80px)',
              textAlign: 'center',
              [BREAKPOINTS.SMALL]: { ...theme.typography['component-bold-l'] },
            }}
          >
            {title}
          </Typography>

          {/* reserve the same space to keep layout balanced */}
          <Box sx={{ minWidth: ICON_WIDTH }}>
            <ProfileMenu
              user={user}
              triggerVariant="light-circle-dark-outline"
            />
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
